import React, { useState, useEffect, useCallback } from 'react';
import { getApiUrl } from '../utils/api';
import { authFetch } from '../utils/authFetch';
import { Project } from '../contexts/ProjectContext';
import { TEMPLATE_NAMES } from '../utils/projectTemplates';
import {
  DEFAULT_TEMPLATE_NAME,
  getCreationSourceAnalytics,
  getInitialSheetSource,
  getProjectCreationSourceValue,
  getTemplateCreationSourceValue,
  type CreationSheetSourceValue,
  type InitialSheetSource,
} from '../utils/projectSheetSource';
import './ProjectSelector.css';
import { trackEvent, trackPageView } from '../statsig';

interface ProjectSelectorProps {
  onProjectSelect: (project: Project, initialSheetSource?: InitialSheetSource) => void;
}

interface RenameDialogState {
  projectId: string;
  currentName: string;
  draftName: string;
}

const ProjectSelector: React.FC<ProjectSelectorProps> = ({ onProjectSelect }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [newProjectName, setNewProjectName] = useState<string>('');
  const [selectedCreationSource, setSelectedCreationSource] = useState<CreationSheetSourceValue>(
    getTemplateCreationSourceValue(DEFAULT_TEMPLATE_NAME)
  );
  const [creatingProject, setCreatingProject] = useState<boolean>(false);
  const [removingProjectIds, setRemovingProjectIds] = useState<string[]>([]);
  const [renamingProjectId, setRenamingProjectId] = useState<string | null>(null);
  const [renameDialog, setRenameDialog] = useState<RenameDialogState | null>(null);

  const fetchProjects = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      const response = await authFetch(`${getApiUrl()}/projects`);
      const data = await response.json();

      if (data.status === 'OK') {
        setProjects(data.projects);
      } else {
        setError(data.message || 'Failed to fetch projects');
      }
    } catch (err) {
      setError('Failed to load projects. Please try again.');
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    trackPageView('project_selector');
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleCreateProject = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!newProjectName.trim()) return;

    try {
      setCreatingProject(true);
      setError('');
      const response = await authFetch(`${getApiUrl()}/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newProjectName.trim() }),
      });

      const data = await response.json();

      if (data.status === 'OK') {
        const initialSheetSource: InitialSheetSource = getInitialSheetSource(selectedCreationSource);
        trackEvent('project_created', {
          project_id: data.project?.id,
          project_name: data.project?.name,
          ...getCreationSourceAnalytics(initialSheetSource),
        });
        onProjectSelect(data.project, initialSheetSource);
      } else {
        setError(data.message || 'Failed to create project');
      }
    } catch (err) {
      setError('Failed to create project. Please try again.');
      console.error('Error creating project:', err);
    } finally {
      setCreatingProject(false);
    }
  };

  const handleDeleteProject = async (projectId: string): Promise<void> => {
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) return;

    // Trigger animation
    setRemovingProjectIds((prev) => [...prev, projectId]);

    setTimeout(async () => {
      try {
        const response = await authFetch(`${getApiUrl()}/projects/${projectId}`, {
          method: 'DELETE',
        });

        const data = await response.json();

        if (data.status === 'OK') {
          trackEvent('project_deleted', {
            project_id: projectId,
          });
          setProjects((prev) => prev.filter((p) => p.id !== projectId));
          setSelectedCreationSource((prev) =>
            prev === getProjectCreationSourceValue(projectId)
              ? getTemplateCreationSourceValue(DEFAULT_TEMPLATE_NAME)
              : prev
          );
        } else {
          setError(data.message || 'Failed to delete project');
        }
      } catch (err) {
        setError('Failed to delete project. Please try again.');
        console.error('Error deleting project:', err);
      } finally {
        setRemovingProjectIds((prev) => prev.filter((id) => id !== projectId));
      }
    }, 300); // match CSS transition duration
  };

  const handleOpenRenameDialog = (projectId: string, currentName: string): void => {
    setRenameDialog({
      projectId,
      currentName,
      draftName: currentName,
    });
    setError('');
  };

  const handleCloseRenameDialog = (): void => {
    setRenameDialog(null);
  };

  const handleRenameProjectSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!renameDialog) return;

    const newName: string = renameDialog.draftName.trim();
    if (!newName) return;
    if (newName === renameDialog.currentName) {
      handleCloseRenameDialog();
      return;
    }

    try {
      setError('');
      setRenamingProjectId(renameDialog.projectId);

      const response = await authFetch(`${getApiUrl()}/projects/${renameDialog.projectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: renameDialog.projectId, newName }),
      });

      const data = await response.json();

      if (data.status === 'OK') {
        trackEvent('project_renamed', {
          project_id: renameDialog.projectId,
          previous_name: renameDialog.currentName,
          next_name: newName,
        });
        setProjects((prev) =>
          prev.map((project) =>
            project.id === renameDialog.projectId ? { ...project, name: newName } : project
          )
        );
        handleCloseRenameDialog();
      } else {
        setError(data.message || 'Failed to rename project');
      }
    } catch (err) {
      setError('Failed to rename project. Please try again.');
      console.error('Error renaming project:', err);
    } finally {
      setRenamingProjectId(null);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="project-selector">
        <div className="project-selector-container">
          <h1>Loading projects...</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="project-selector">
      <div className="project-selector-container">
        <h1>Welcome Back 👋</h1>
        <p className="subtitle">Your creative space awaits — pick a project or start fresh</p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleCreateProject} className="create-project-form">
          <input
            type="text"
            placeholder="New project name"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            disabled={creatingProject}
            className="project-name-input"
          />
          <select
            value={selectedCreationSource}
            onChange={(e) => setSelectedCreationSource(e.target.value as CreationSheetSourceValue)}
            disabled={creatingProject}
            className="template-select"
          >
            <option className="template-select-section" value="templates-header" disabled>
              Site Templates
            </option>
            {TEMPLATE_NAMES.map((name) => (
              <option key={name} value={getTemplateCreationSourceValue(name)}>{name}</option>
            ))}
            {projects.length > 0 && (
              <>
                <option className="template-select-section" value="projects-header" disabled>
                  Copy Existing Project
                </option>
                {projects.map((project) => (
                  <option key={project.id} value={getProjectCreationSourceValue(project.id)}>
                    {project.name}
                  </option>
                ))}
              </>
            )}
          </select>
          <button type="submit" disabled={creatingProject || !newProjectName.trim()} className="create-button">
            {creatingProject ? 'Creating...' : 'Create Project'}
          </button>
        </form>

        <div className="projects-list">
          {projects.length === 0 ? (
            <div className="no-projects">
              <p>You don't have any projects yet.</p>
              <p>Create one to get started!</p>
            </div>
          ) : (
            projects.map((project) => {
              const isRenaming: boolean = renamingProjectId === project.id;
              return (
                <div
                  key={project.id}
                  className={`project-card ${removingProjectIds.includes(project.id) ? 'removing' : ''}`}
                >
                  <div className="project-info">
                    <h3>{project.name}</h3>
                    <p className="project-date">Created: {formatDate(project.created_at)}</p>
                    <p className="project-users">Users: {project.allowed_users.join(', ')}</p>
                  </div>
                  <div className="project-actions">
                    <button
                      onClick={() => {
                        trackEvent('project_selected', {
                          project_id: project.id,
                          project_name: project.name,
                        });
                        onProjectSelect(project);
                      }}
                      className="select-button"
                    >
                      Select
                    </button>
                    <button
                      onClick={() => handleOpenRenameDialog(project.id, project.name)}
                      className="rename-button"
                      disabled={isRenaming}
                    >
                      {isRenaming ? 'Renaming...' : 'Rename'}
                    </button>
                    <button onClick={() => handleDeleteProject(project.id)} className="delete-button">Delete</button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {renameDialog && (
        <div className="rename-modal-overlay" onClick={handleCloseRenameDialog}>
          <div className="rename-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Rename Project</h2>
            <form onSubmit={handleRenameProjectSubmit}>
              <input
                type="text"
                value={renameDialog.draftName}
                onChange={(e) =>
                  setRenameDialog((prev) =>
                    prev ? { ...prev, draftName: e.target.value } : prev
                  )
                }
                className="project-name-input"
                autoFocus
              />
              <div className="rename-modal-actions">
                <button type="button" className="rename-cancel-button" onClick={handleCloseRenameDialog}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rename-save-button"
                  disabled={
                    renamingProjectId === renameDialog.projectId ||
                    !renameDialog.draftName.trim() ||
                    renameDialog.draftName.trim() === renameDialog.currentName
                  }
                >
                  {renamingProjectId === renameDialog.projectId ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectSelector;
